// @mui
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Iconify from '../Iconify';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import pets from '../sidebar/pets.json';
import {
  useGetUserPetsQuery,
  useSelectPetForPlayerMutation,
} from '../../generated/graphql';
import { useStationAuth } from '../../contexts/StationAuthContext';

interface InventoryDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export function InventoryDialog({ open, setOpen }: InventoryDialogProps) {
  const { playerId, setActivePet, activePet } = useStationAuth();
  const [firstLoad, setFirstLoad] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const newpet = sessionStorage.getItem('newPet');

  const [{ data: userPetsData }, reexecuteQuery] = useGetUserPetsQuery({
    variables: {
      playerId: playerId || '',
    },
    pause: !playerId,
  });

  useEffect(() => {
    if (open && playerId && reexecuteQuery) {
      reexecuteQuery({ requestPolicy: 'network-only' });
    }
  }, [open, playerId, reexecuteQuery]);

  const userPets = userPetsData?.getUserPets;

  useEffect(() => {
    if (!firstLoad && userPets && userPets.length > 0) {
      setFirstLoad(true);
      setActivePet(userPets.filter((p) => p.isActive)[0] || userPets[0]);
    }
  }, [userPets, firstLoad]);

  const unlockedPets = userPetsData?.getUserPets.map((p) => p.petId);

  const dogs = pets.filter((p) => p.type === 'DOG');
  const cats = pets.filter((p) => p.type === 'CAT');
  const pigeons = pets.filter((p) => p.type === 'PIGEON');
  const reptiles = pets.filter((p) => p.type === 'REPTILE');
  const xmas = pets.filter((p) => p.type === 'XMAS');
  const easter = pets.filter((p) => p.type === 'EASTER');
  const stpatrick = pets.filter((p) => p.type === 'STPATRICK');
  const usa = pets.filter((p) => p.type === 'USA');
  const cinco = pets.filter((p) => p.type === 'CINCODEMAYO');

  const countPets = (petId: string) =>
    userPets?.filter((p) => p.petId === petId).length || 0;

  const [{ data: selectPetData }, selectPet] = useSelectPetForPlayerMutation();

  const handleSelectPet = (pet: any) => {
    const dbPet = userPets?.find((p) => p.petId === pet.id);
    if (dbPet) {
      setActivePet(dbPet);
      selectPet({ playerId: playerId || '', petId: dbPet.id });
      setOpen(false);
    }

    sessionStorage.clear();
  };

  const ribbonColors: Record<string, string> = {
    COMMON: '#8FA9FF',
    RARE: '#E9A5FF',
    LEGENDARY: '#FFF3AE',
  };

  // Responsive sizes
  const petCardSize = isMobile ? 100 : 170;
  const petImgSize = isMobile ? 80 : 140;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      sx={{ padding: isMobile ? '0px 0px 70px' : 0 }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', textAlign: 'center' }}>
        <Box display={'flex'}>
          <Iconify
            icon="tabler:flame-filled"
            sx={{
              margin: 'auto 5px',
              height: isMobile ? '20px' : '40px',
              width: '30px',
            }}
          />
          <Typography
            sx={{
              fontSize: isMobile ? '18px' : '25px',
              fontWeight: 'bold',
            }}
          >
            My Inventory
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: isMobile ? '5px' : '20px',
          maxHeight: '70vh',
          overflowY: 'auto',

          scrollbarWidth: 'none',
          '-ms-overflow-style': 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {[
          { title: 'Dogs', pets: dogs },
          { title: 'Cats', pets: cats },
          { title: 'Birds', pets: pigeons },
          { title: 'Reptiles & Amphibians', pets: reptiles },
          { title: 'Cinco de Mayo', pets: cinco },
          { title: 'Easter', pets: easter },
          { title: 'Xmas', pets: xmas },
          { title: 'St. Patrick', pets: stpatrick },
          { title: 'USA', pets: usa },
        ].map(({ title, pets: categoryPets }) => {
          const ownedPets = categoryPets.filter((p) =>
            unlockedPets?.includes(p.id)
          );
          return (
            <Box
              key={title}
              sx={{
                backgroundColor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
                justifyContent: 'center',
                borderRadius: '20px',
                marginTop: '20px',
                paddingLeft: isMobile ? '10px' : '25px',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    color: 'black',
                    fontSize: '25px',
                    marginTop: '15px',
                  }}
                >
                  {title}
                </Typography>
                <Chip
                  sx={{
                    backgroundColor: '#FE3780',
                    fontWeight: 'bold',
                    margin: 2,
                    fontSize: '20px',
                  }}
                  label={`${ownedPets.length}/${categoryPets.length}`}
                />
              </Box>
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  display: 'flex',
                  flexWrap: 'wrap',
                  borderRadius: '20px',
                  paddingBottom: isMobile ? '10px' : '30px',
                  width: '100%',
                }}
              >
                {categoryPets.map((p) => {
                  const isOwned = unlockedPets?.includes(p.id);
                  const petCount = countPets(p.id);
                  const isSelected = activePet?.petId === p.id;

                  return (
                    <>
                      <Box
                        key={p.id}
                        sx={{
                          width: `${petCardSize}px`,
                          height: `${petCardSize}px`,
                          backgroundColor: isOwned
                            ? resolveBgColor(p.rarity)
                            : '#EAEAEA',
                          margin: '5px',
                          alignContent: 'center',
                          borderRadius: '20px',
                          cursor: isOwned ? 'pointer' : 'default',
                          border: isSelected
                            ? isMobile
                              ? '4px solid #68FFAC'
                              : '8px solid #68FFAC'
                            : 'none',
                          position: 'relative',
                          boxSizing: 'border-box',
                        }}
                        onClick={() => isOwned && handleSelectPet(p)}
                      >
                        {newpet === p.id && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '-5px',
                              right: '-10px',
                              zIndex: 20,
                              width: isMobile ? '35px' : '50px',
                              height: isMobile ? '20px' : '25px',
                              backgroundColor: '#ec24a0ff',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              transform: 'skew(-20deg)',
                              borderRadius: '2px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: isMobile ? '12px' : '18px',
                                fontWeight: 'bold',
                              }}
                            >
                              NEW
                            </Typography>
                          </Box>
                        )}
                        {isOwned ? (
                          <>
                            <img
                              src={pets.find((p2) => p2.id === p.id)?.img}
                              width={`${petImgSize}px`}
                              style={{ margin: 'auto', display: 'block' }}
                            />
                            {petCount > 1 && !isSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '-5px',
                                  right: '-10px',
                                  width: isMobile ? '35px' : '50px',
                                  height: isMobile ? '20px' : '25px',
                                  backgroundColor: ribbonColors[p.rarity],
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  transform: 'skew(-20deg)',
                                  borderRadius: '2px',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  x{petCount}
                                </Typography>
                              </Box>
                            )}
                            {isSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: isMobile ? '-10px' : '-15px',
                                  right: '-15px',
                                  width: isMobile ? '40px' : '50px',
                                  height: isMobile ? '20px' : '25px',
                                  backgroundColor: '#68FFAC',
                                  display: 'flex',
                                  fontWeight: 'bold',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  transform: 'skew(-15deg)',
                                  borderRadius: '2px',
                                }}
                              >
                                <Iconify
                                  icon="material-symbols:check"
                                  color="#006400"
                                  sx={{ width: '20px', height: '20px' }}
                                />
                              </Box>
                            )}
                          </>
                        ) : (
                          <Iconify
                            icon="material-symbols:lock"
                            color="#aeaeaeff"
                            sx={{
                              margin: 'auto',
                              width: isMobile ? '50px' : '75px',
                              height: isMobile ? '50px' : '75px',
                            }}
                          />
                        )}
                      </Box>
                    </>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </DialogContent>
    </Dialog>
  );
}

function resolveBgColor(rarity: string) {
  switch (rarity) {
    case 'COMMON':
      return '#406CFF';
    case 'RARE':
      return '#D34DFF';
    case 'LEGENDARY':
      return '#FFD900';
    default:
      return '#EAEAEA';
  }
}
